import { ChangeDetectionStrategy, Component, signal, inject, input, output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { AbstractControl, FormBuilder, FormsModule, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { Button } from 'primeng/button';
import { Tooltip } from 'primeng/tooltip';
import { InputText } from 'primeng/inputtext';
import { InputMask } from 'primeng/inputmask';
import { SelectButton } from 'primeng/selectbutton';
import { Select } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { Dialog } from 'primeng/dialog';
import { MessageService } from 'primeng/api';
import { ConsultorioCompartilhamentoComponent } from '../consultorio-compartilhamento/consultorio-compartilhamento';
import { Pessoa } from '@/shared/models/pessoa-info.model';
import { ConsultorioPrivadoService } from '@/core/api/sicom-api/registro/medico/consultorio-privado/consultorio-privado.service';
import {
    ConsultorioPrivadoCreateRequest,
    MedicoConsultorioCompartilhamentoCreateRequest,
    MedicoConsultorioEspecialidadeCreateRequest,
    MedicoConsultorioHorarioCreateRequest,
    MedicoConsultorioTelefoneCreateRequest
} from '@/core/api/sicom-api/registro/medico/consultorio-privado/consultorio-privado.model';
import { SelectMunicipio } from '@/shared/components/layout/select-municipio/select-municipio';
import { EspecialidadeService } from '@/core/api/sicom-api/registro/medico/especialidade/especialidade.service';
import { AreaAtuacaoPorEspecialidadeResponse, EspecialidadeListaResponse } from '@/core/api/sicom-api/registro/medico/especialidade/especialidade.model';
import { LocalidadesService } from '@/core/api/consulta-api/localidades/localidades.service';
import { MunicipioService } from '@/core/api/sicom-api/registro/medico/municipio/municipio.service';
import { MunicipioResponse } from '@/core/api/sicom-api/registro/medico/municipio/municipio.model';
import { FormularioValidatorUtil } from '@/shared/utils/formulario-validator-util';

interface EspecialidadeListaItem {
    idEspecialidade: number;
    dsEspecialidade: string;
}

interface EspecialidadeSelecionadaItem {
    idEspecialidade: number;
    dsEspecialidade: string;
    idAreaAtuacao: number;
    dsAreaAtuacao: string;
    idEspecArea: number;
}

import { CardContent } from '@/shared/components/card-content/card-content';
import { SwitchSimNao } from '@/shared/components/switch-sim-nao/switch-sim-nao';
import { comIdempotencia, novaChaveIdempotencia } from 'src/interceptors/idempotencia-interceptor';

import { DialogoPadraoDirective } from '@/shared/components/layout/dialogo-padrao/dialogo-padrao.directive';
@Component({
    selector: 'app-consultorio-create-dialog',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [DialogoPadraoDirective, SwitchSimNao, FormsModule, ReactiveFormsModule, Button, Tooltip, InputText, InputMask, SelectButton, Select, TableModule, Dialog, ConsultorioCompartilhamentoComponent, SelectMunicipio, CardContent],
    templateUrl: './consultorio-create-dialog.html',
    styleUrl: './consultorio-create-dialog.scss'
})
/** Assistente (stepper) de cadastro de consultório privado. */
export class ConsultorioCreateDialogComponent {
    pessoa = input<Pessoa>();
    cancelarCadastro = output<void>();
    consultorioCreated = output<void>();

    criando = signal(false);
    dialogMunicipioVisivel = false;
    municipioDialogId: number | null = null;
    municipioSelecionadoLabel = signal('');

    private readonly fb = inject(FormBuilder);
    private readonly consultorioService = inject(ConsultorioPrivadoService);
    private readonly especialidadeService = inject(EspecialidadeService);
    private readonly localidadesService = inject(LocalidadesService);
    private readonly municipioService = inject(MunicipioService);
    private readonly messageService = inject(MessageService);

    novoStep = 0;

    /** Formulário tipado dos dados do consultório (etapa 0 do wizard); ReactiveForms cuida do CD ao autopreencher via CEP. */
    novoForm = this.fb.group({
        idMunicipio: this.fb.control<number | null>(null),
        txEndereco: this.fb.nonNullable.control('', Validators.required),
        nmBairro: this.fb.nonNullable.control(''),
        nuCep: this.fb.nonNullable.control(''),
        inDivulga: this.fb.nonNullable.control(false),
        nuCnes: this.fb.nonNullable.control(''),
        inUso: this.fb.control<'E' | 'C' | null>(null, Validators.required),
        inEspecializado: this.fb.control<boolean | null>(null, Validators.required),
        nuRegistroSanitario: this.fb.nonNullable.control(''),
        inTipoRegSanitario: this.fb.nonNullable.control<'M' | 'E'>('M'),
        dtValidadeRegSanitario: this.fb.nonNullable.control('', this.validarDataMaiorOuIgualHoje),
        // Nº do Alvará é obrigatório a menos que "Isento" (o toggle desabilita o controle e a validação de required não roda em controles desabilitados).
        nuAlvara: this.fb.nonNullable.control('', Validators.required),
        inIsencaoAlvara: this.fb.nonNullable.control(false),
        dtValidadeAlvara: this.fb.nonNullable.control('', this.validarDataMaiorOuIgualHoje)
    });

    /** Telefones e horários adicionados no wizard (grades geridas por botões Adicionar/Remover). */
    novoTelefones = signal<MedicoConsultorioTelefoneCreateRequest[]>([]);
    novoHorarios = signal<MedicoConsultorioHorarioCreateRequest[]>([]);

    // Tipos de telefone (padrão usado no projeto)
    tiposTelefone = [
        { label: 'Fixo', value: 'F' },
        { label: 'Celular', value: 'C' },
        { label: 'Residencial', value: 'R' },
        { label: 'Trabalho', value: 'T' }
    ];

    diasSemana = [
        { label: 'Domingo', value: 1 },
        { label: 'Segunda-feira', value: 2 },
        { label: 'Terça-feira', value: 3 },
        { label: 'Quarta-feira', value: 4 },
        { label: 'Quinta-feira', value: 5 },
        { label: 'Sexta-feira', value: 6 },
        { label: 'Sábado', value: 7 }
    ];

    // Formulário temporário para adicionar/editar telefone
    novoTelefone: MedicoConsultorioTelefoneCreateRequest = {
        nuDdd: '',
        nuTelefone: '',
        inTipoTelefone: 'F',
        txObservacao: '',
        nuRamal: ''
    };

    novoHorario: MedicoConsultorioHorarioCreateRequest = {};

    // Índice em edição na grade (-1 = adicionando)
    editingTelefoneIndex = -1;
    editingHorarioIndex = -1;

    // Especialidades (wizard etapa condicional)
    especialidadesLista = signal<EspecialidadeListaItem[]>([]);
    areasAtuacaoLista = signal<AreaAtuacaoPorEspecialidadeResponse[]>([]);
    especialidadesSelecionadas: EspecialidadeSelecionadaItem[] = [];
    especialidadeSelecionada: number | null = null; // idEspecialidade
    areaAtuacao: number | null = null; // idAreaAtuacao

    // Compartilhamentos adicionados no wizard (quando inUso = 'C')
    novoCompartilhamentos: MedicoConsultorioCompartilhamentoCreateRequest[] = [];

    constructor() {
        // "Isento" bloqueia a Data de Validade E o Nº do Alvará (padrão reativo, sem [disabled] no formControlName).
        // O nº é limpo ao marcar isento, pois o payload/validação usam getRawValue() (incluem controle desabilitado).
        this.novoForm.controls.inIsencaoAlvara.valueChanges.pipe(takeUntilDestroyed()).subscribe((isento) => {
            const validade = this.novoForm.controls.dtValidadeAlvara;
            const numero = this.novoForm.controls.nuAlvara;
            if (isento) {
                validade.disable({ emitEvent: false });
                numero.setValue('', { emitEvent: false });
                numero.disable({ emitEvent: false });
            } else {
                validade.enable({ emitEvent: false });
                numero.enable({ emitEvent: false });
            }
        });

        // CEP com 8 dígitos autopreenche endereço/município (patchValue → CVA atualiza os inputs, sem detecção manual).
        this.novoForm.controls.nuCep.valueChanges.pipe(takeUntilDestroyed()).subscribe((cep) => this.onCepChange(cep ?? ''));

        // Campos numéricos: mantém apenas dígitos.
        (['nuCnes', 'nuRegistroSanitario', 'nuAlvara'] as const).forEach((campo) => {
            this.novoForm.controls[campo].valueChanges.pipe(takeUntilDestroyed()).subscribe((valor) => this.apenasNumeros(campo, valor ?? ''));
        });
    }

    get numeroTelefoneMask(): string {
        // Celular geralmente possui 9 dígitos
        return this.novoTelefone.inTipoTelefone === 'C' ? '99999-9999' : '9999-9999';
    }

    get novoMaxStep(): number {
        // 0=Dados, 1=Tel, 2=Horario
        let max = 2;
        if (this.novoForm.controls.inUso.value === 'C') max += 1;
        if (this.novoForm.controls.inEspecializado.value) max += 1;
        return max;
    }

    get compartStepIndex(): number | null {
        return this.novoForm.controls.inUso.value === 'C' ? 3 : null;
    }

    get especialStepIndex(): number | null {
        if (!this.novoForm.controls.inEspecializado.value) return null;
        return this.novoForm.controls.inUso.value === 'C' ? 4 : 3;
    }

    get etapasCadastro(): string[] {
        const etapas = ['Dados do Consultório', 'Telefone', 'Horários'];
        if (this.novoForm.controls.inUso.value === 'C') etapas.push('Compartilhamento');
        if (this.novoForm.controls.inEspecializado.value) etapas.push('Especialidades');
        return etapas;
    }

    /**
     * Chave de idempotência da sessão de cadastro. Aqui NÃO precisa girar na mão: o pai usa
     * `@if (mostrandoTelaCadastro)`, então o componente é criado ao abrir e destruído ao
     * fechar — a instância já é a sessão. Se um dia virar diálogo persistente, passa a
     * precisar de rotação explícita ao abrir, senão o usuário só cadastra um por TTL.
     */
    private readonly chaveIdempotencia = novaChaveIdempotencia();

    ngOnInit(): void {
        this.resetNovo();
        this.loadEspecialidades();
    }

    abrirDialogMunicipio(): void {
        this.municipioDialogId = this.novoForm.controls.idMunicipio.value;
        this.dialogMunicipioVisivel = true;
    }

    fecharDialogMunicipio(): void {
        this.dialogMunicipioVisivel = false;
        this.municipioDialogId = this.novoForm.controls.idMunicipio.value;
    }

    confirmarMunicipioDialog(): void {
        this.novoForm.controls.idMunicipio.setValue(this.municipioDialogId);
        this.dialogMunicipioVisivel = false;
        this.atualizarLabelMunicipioSelecionado(this.municipioDialogId);
    }

    close(): void {
        this.cancelarCadastro.emit();
    }

    /** Valor inicial (limpo) do formulário do consultório. */
    private getInitialFormValue(): ReturnType<ConsultorioCreateDialogComponent['novoForm']['getRawValue']> {
        return {
            idMunicipio: null,
            txEndereco: '',
            nmBairro: '',
            nuCep: '',
            inDivulga: false,
            nuCnes: '',
            inUso: null,
            inEspecializado: null,
            nuRegistroSanitario: '',
            inTipoRegSanitario: 'M',
            dtValidadeRegSanitario: '',
            nuAlvara: '',
            inIsencaoAlvara: false,
            dtValidadeAlvara: ''
        };
    }

    getTipoLabel(value: string | number, opts: { label: string; value: string | number }[]): string {
        const v = (value ?? '').toString();
        return opts.find((o) => String(o.value) === v)?.label ?? v;
    }

    private apenasNumeros(campo: 'nuCnes' | 'nuRegistroSanitario' | 'nuAlvara', valor: string): void {
        const valorNumerico = valor.replace(/\D+/g, '');
        if (valor !== valorNumerico) {
            this.novoForm.controls[campo].setValue(valorNumerico, { emitEvent: false });
        }
    }

    adicionarTelefone(): void {
        const ddd = (this.novoTelefone.nuDdd || '').replace(/\D/g, '').slice(0, 2);
        const numero = (this.novoTelefone.nuTelefone || '').replace(/\D/g, '');
        const tipo = this.novoTelefone.inTipoTelefone;
        const observacao = (this.novoTelefone.txObservacao || '').trim();
        const ramal = (this.novoTelefone.nuRamal || '').replace(/\D/g, '').slice(0, 4);

        if (!ddd || ddd.length !== 2) return;
        if (!numero || (tipo === 'C' ? numero.length !== 9 && numero.length !== 10 : numero.length !== 8)) return;

        const numeroFmt = tipo === 'C' ? `${numero.padStart(9, '0').slice(0, 5)}-${numero.padStart(9, '0').slice(5, 9)}` : `${numero.padStart(8, '0').slice(0, 4)}-${numero.padStart(8, '0').slice(4, 8)}`;

        const item: MedicoConsultorioTelefoneCreateRequest = { nuDdd: ddd, nuTelefone: numeroFmt, inTipoTelefone: tipo, txObservacao: observacao, nuRamal: ramal };

        if (this.editingTelefoneIndex >= 0) {
            const index = this.editingTelefoneIndex;
            this.novoTelefones.update((tels) => tels.map((t, i) => (i === index ? item : t)));
        } else {
            this.novoTelefones.update((tels) => [...tels, item]);
        }
        this.cancelarEdicaoTelefone();
    }

    editarTelefone(index: number): void {
        const it = this.novoTelefones()[index];
        if (!it) return;
        this.editingTelefoneIndex = index;
        this.novoTelefone = {
            nuDdd: it.nuDdd,
            nuTelefone: it.nuTelefone,
            inTipoTelefone: it.inTipoTelefone,
            txObservacao: it.txObservacao ?? '',
            nuRamal: it.nuRamal ?? ''
        };
    }

    removerTelefone(index: number): void {
        if (index < 0 || index >= this.novoTelefones().length) return;
        this.novoTelefones.update((tels) => tels.filter((_, i) => i !== index));
        if (this.editingTelefoneIndex === index) this.cancelarEdicaoTelefone();
    }

    cancelarEdicaoTelefone(): void {
        this.editingTelefoneIndex = -1;
        this.novoTelefone = { nuDdd: '', nuTelefone: '', inTipoTelefone: 'F', txObservacao: '', nuRamal: '' };
    }

    // Horários
    adicionarHorario(): void {
        if (!this.novoHorario.idDiaSemanaInicio || !this.novoHorario.idDiaSemanaFim) return;
        const item: MedicoConsultorioHorarioCreateRequest = { ...this.novoHorario };
        if (this.editingHorarioIndex >= 0) {
            const index = this.editingHorarioIndex;
            this.novoHorarios.update((horarios) => horarios.map((h, i) => (i === index ? item : h)));
        } else {
            this.novoHorarios.update((horarios) => [...horarios, item]);
        }
        this.cancelarEdicaoHorario();
    }

    editarHorario(index: number): void {
        this.novoHorario = { ...this.novoHorarios()[index] };
        this.editingHorarioIndex = index;
    }

    removerHorario(index: number): void {
        this.novoHorarios.update((horarios) => horarios.filter((_, i) => i !== index));
        if (this.editingHorarioIndex === index) this.cancelarEdicaoHorario();
    }

    cancelarEdicaoHorario(): void {
        this.editingHorarioIndex = -1;
        this.novoHorario = {};
    }

    criarConsultorio(): void {
        const p = this.pessoa();
        if (!p || typeof p.id !== 'number' || !p.uf) return;

        const v = this.novoForm.getRawValue();

        // Valida mínimos
        const endereco = (v.txEndereco || '').trim();
        const bairro = (v.nmBairro || '').trim();
        const cep = (v.nuCep || '').replace(/\D/g, '');

        if (!v.idMunicipio) {
            this.mostrarErroValidacao('O preenchimento do município é obrigatório');
            return;
        }

        if (!endereco) {
            this.mostrarErroValidacao('O preenchimento do endereço é obrigatório');
            return;
        }

        if (!bairro) {
            this.mostrarErroValidacao('O preenchimento do bairro é obrigatório');
            return;
        }

        if (!cep) {
            this.mostrarErroValidacao('O preenchimento do CEP é obrigatório');
            return;
        }

        const body: ConsultorioPrivadoCreateRequest = this.mapCreateBody();
        this.criando.set(true);
        this.consultorioService.inserir({ id: p.id, uf: p.uf }, body, comIdempotencia(this.chaveIdempotencia)).subscribe({
            next: () => {
                this.resetNovo();
                this.consultorioCreated.emit();
            },
            error: () => {},
            complete: () => {
                this.criando.set(false);
            }
        });
    }

    nextNovo(): void {
        // Validar obrigatórios ao sair do Passo 0 (Dados do Consultório)
        if (this.novoStep === 0) {
            if (!this.validateStep0()) return;
        }
        if (this.novoStep < this.novoMaxStep) this.novoStep += 1;
    }

    prevNovo(): void {
        if (this.novoStep > 0) this.novoStep -= 1;
    }

    // Dispara ao alterar o CEP; quando completar 8 dígitos, busca dados e preenche endereço/município.
    // ReactiveForms atualiza os inputs via CVA e os signals disparam o CD automaticamente.
    onCepChange(valor: string): void {
        const cep = (valor || '').replace(/\D/g, '');
        if (cep.length !== 8) return;

        this.localidadesService.get(cep).subscribe({
            next: (resp) => {
                if (!resp || !((resp.logradouro || '').trim() || (resp.bairro || '').trim() || (resp.municipio || '').trim())) {
                    // CEP consultado mas sem endereço correspondente.
                    this.avisarFalhaCep('CEP não encontrado. Verifique o número informado.');
                    return;
                }
                this.novoForm.patchValue({
                    txEndereco: (resp.logradouro || '').trim(),
                    nmBairro: (resp.bairro || '').trim()
                });

                const ibgeStr = (resp.ibge || '').toString().trim();
                const municipioNome = (resp.municipio || '').trim();
                const uf = (resp.uf || '').trim();

                this.municipioService.listarMunicipios().subscribe({
                    next: (lista: MunicipioResponse[]) => {
                        let id: number | null = null;
                        if (Array.isArray(lista) && lista.length) {
                            if (ibgeStr) {
                                const ibgeNum = Number(ibgeStr);
                                const byIbge = lista.find((m) => m.coIbge === ibgeNum);
                                if (byIbge) id = byIbge.id;
                            }
                            if (id == null && municipioNome) {
                                const norm = (s: string) =>
                                    s
                                        .normalize('NFD')
                                        .replace(/[\u0300-\u036f]/g, '')
                                        .toLowerCase()
                                        .trim();
                                const nomeNorm = norm(municipioNome);
                                const ufNorm = (uf || '').toUpperCase();
                                const byName = lista.find((m) => norm(m.dsMunicipio) === nomeNorm && (!ufNorm || (m.uf || '').toUpperCase() === ufNorm));
                                if (byName) id = byName.id;
                            }
                        }
                        this.novoForm.controls.idMunicipio.setValue(id);
                        const municipioSelecionado = (lista || []).find((m) => m.id === id) ?? null;
                        this.municipioSelecionadoLabel.set(municipioSelecionado ? `${municipioSelecionado.dsMunicipio} - ${municipioSelecionado.uf ?? ''}`.trim() : '');
                    },
                    error: () => {
                        // falha silenciosa, não impede o preenchimento dos demais campos
                    }
                });
            },
            error: (err) => {
                // Falha ao consultar o serviço de CEP (indisponível/sem autorização/CEP inválido).
                // Antes o erro era engolido silenciosamente, o que dava a impressão de "nada acontece"
                // — como os campos Endereço/Bairro são somente-leitura, o usuário ficava sem feedback.
                console.error('Erro ao buscar endereço pelo CEP', err);
                this.avisarFalhaCep('Não foi possível buscar o endereço para este CEP no momento.');
            }
        });
    }

    /** Feedback ao usuário quando a busca automática de endereço pelo CEP não retorna dados. */
    private avisarFalhaCep(mensagem: string): void {
        this.messageService.add({ severity: 'warn', summary: 'CEP', detail: mensagem });
    }

    private mapCreateBody(): ConsultorioPrivadoCreateRequest {
        const p = this.pessoa()!;
        const v = this.novoForm.getRawValue();
        const b: ConsultorioPrivadoCreateRequest = {
            // identificador do solicitante e UF
            idSolicitante: Number(p.id),
            sgUf: String(p.uf),

            // endereço obrigatório
            idMunicipio: v.idMunicipio as number,
            txEndereco: v.txEndereco?.trim(),
            nmBairro: v.nmBairro?.trim(),
            nuCep: (v.nuCep || '').replace(/\D/g, ''),
            inDivulga: v.inDivulga ? 'S' : 'N'
        };

        // Campos opcionais
        if (v.nuCnes?.trim()) b.nuCnes = v.nuCnes.trim();
        b.inEspecializado = v.inEspecializado ? 'S' : 'N';
        if (v.inUso) b.inUso = v.inUso;

        if (v.nuRegistroSanitario?.trim()) b.nuRegistroSanitario = v.nuRegistroSanitario.trim();
        if (v.inTipoRegSanitario) b.inTipoRegSanitario = v.inTipoRegSanitario;
        if (v.dtValidadeRegSanitario?.trim()) {
            const iso = this.toIsoDate(v.dtValidadeRegSanitario.trim());
            if (iso) b.dtValidadeRegSanitario = iso;
        }

        if (v.nuAlvara?.trim()) b.nuAlvara = v.nuAlvara.trim();
        b.inIsencaoAlvara = v.inIsencaoAlvara ? 'S' : 'N';
        if (v.dtValidadeAlvara?.trim()) {
            const iso = this.toIsoDate(v.dtValidadeAlvara.trim());
            if (iso) b.dtValidadeAlvara = iso;
        }
        // Especialidades (se marcado como especializado): montar lista com dtInicio obrigatório (data atual)
        if (v.inEspecializado) {
            const hojeIso = this.todayIsoDate();
            const especs = this.buildEspecialidadesCreate(hojeIso);
            if (especs.length > 0) b.especialidades = especs;
        }

        // Compartilhamentos: se uso = compartilhado, incluir lista capturada do componente
        if (v.inUso === 'C' && this.novoCompartilhamentos?.length) {
            b.compartilhamentos = this.novoCompartilhamentos;
        }

        // Telefones
        if (this.novoTelefones().length) {
            b.telefones = this.novoTelefones();
        }

        // Horários
        if (this.novoHorarios().length) {
            b.horarios = this.novoHorarios();
        }

        return b;
    }

    private validateStep0(): boolean {
        const erros: string[] = [];
        const v = this.novoForm.getRawValue();
        const uso = v.inUso;
        const espec = v.inEspecializado;
        const endereco = (v.txEndereco || '').trim();
        const nuAlvara = (v.nuAlvara || '').trim();
        const isento = !!v.inIsencaoAlvara;
        const dtValidadeRegSanitario = (v.dtValidadeRegSanitario || '').trim();
        const dtValidadeAlvara = (v.dtValidadeAlvara || '').trim();

        // 1) Tipo de uso obrigatório
        if (!(uso === 'E' || uso === 'C')) {
            erros.push('O preenchimento do tipo de uso é obrigatório');
        }

        // 2) Tipo de especialidade obrigatório (Sim/Não)
        if (espec === undefined || espec === null) {
            erros.push('O preenchimento do tipo de especialidade é obrigatório');
        }

        // 3) Endereço obrigatório (campo Endereço)
        if (!endereco) {
            erros.push('O preenchimento do endereço é obrigatório');
        }

        // 4) Se não há nº do Alvará, deve marcar Isento
        if (!nuAlvara && !isento) {
            erros.push('Caso o consultório não tenha alvará, marque o campo isento!');
        }

        if (dtValidadeRegSanitario && !FormularioValidatorUtil.dataMaiorOuIgualHoje(dtValidadeRegSanitario)) {
            erros.push('A Data de Validade do Registro Sanitário deve ser igual ou superior à data de hoje.');
        }

        if (!isento && dtValidadeAlvara && !FormularioValidatorUtil.dataMaiorOuIgualHoje(dtValidadeAlvara)) {
            erros.push('A Data de Validade do Alvará deve ser igual ou superior à data de hoje.');
        }

        if (erros.length) {
            this.novoForm.markAllAsTouched();
            this.exibirErrosValidacao(erros);
            return false;
        }

        return true;
    }

    /** Validador de data opcional: vazio é válido; preenchido, precisa ser data válida e >= hoje. */
    private validarDataMaiorOuIgualHoje(control: AbstractControl): ValidationErrors | null {
        const valor = (control.value ?? '').toString().trim();
        if (!valor) return null;
        return FormularioValidatorUtil.dataMaiorOuIgualHoje(valor) ? null : { dataInvalida: true };
    }

    campoInvalido(nomeCampo: string): boolean {
        const campo = this.novoForm.get(nomeCampo);
        return !!campo && campo.invalid && (campo.touched || campo.dirty);
    }

    mensagemErroCampo(nomeCampo: string): string | null {
        const campo = this.novoForm.get(nomeCampo);
        if (!campo?.errors) return null;

        if (campo.errors['required']) return 'Campo obrigatório.';
        if (campo.errors['dataInvalida']) return 'Data inválida.';
        return 'Valor inválido.';
    }

    private exibirErrosValidacao(erros: string[]): void {
        for (const mensagem of erros) {
            this.mostrarErroValidacao(mensagem);
        }
    }

    private mostrarErroValidacao(mensagem: string): void {
        this.messageService.add({ severity: 'error', summary: 'Aviso', detail: mensagem });
    }

    private toIsoDate(ddmmaa: string): string | null {
        // Espera dd/mm/aaaa
        const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(ddmmaa);
        if (!m) return null;
        const [_, d, mth, y] = m;
        return `${y}-${mth}-${d}`;
    }

    private todayIsoDate(): string {
        const dt = new Date();
        const yyyy = dt.getFullYear();
        const mm = String(dt.getMonth() + 1).padStart(2, '0');
        const dd = String(dt.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
    }

    private buildEspecialidadesCreate(dtInicioIso: string): MedicoConsultorioEspecialidadeCreateRequest[] {
        const idEspecAreas = new Set<number>();
        for (const especialidade of this.especialidadesSelecionadas) {
            if (especialidade.idEspecArea != null) idEspecAreas.add(especialidade.idEspecArea);
        }

        return Array.from(idEspecAreas).map((id) => ({ idEspecArea: id, dtInicio: dtInicioIso }));
    }

    onCompartRowsChange(rows: { idSolicitante: number; entrada?: string | null; saida?: string | null }[]): void {
        // Converte datas dd/MM/yyyy para ISO (YYYY-MM-DD)
        this.novoCompartilhamentos = (rows || []).map((r) => {
            const out: MedicoConsultorioCompartilhamentoCreateRequest = {
                idSolicitante: Number(r.idSolicitante ?? 0)
            };
            if (r.entrada) {
                const iso = this.toIsoDate(r.entrada);
                if (iso) out.dtInicio = iso;
            }
            if (r.saida) {
                const iso = this.toIsoDate(r.saida);
                if (iso) out.dtFim = iso;
            }
            return out;
        });
    }

    private resetNovo(): void {
        this.novoForm.controls.dtValidadeAlvara.enable({ emitEvent: false });
        this.novoForm.controls.nuAlvara.enable({ emitEvent: false });
        this.novoForm.reset(this.getInitialFormValue());
        this.novoStep = 0;
        this.dialogMunicipioVisivel = false;
        this.municipioDialogId = null;
        this.municipioSelecionadoLabel.set('');
        this.novoTelefones.set([]);
        this.novoHorarios.set([]);
        this.cancelarEdicaoTelefone();
        this.cancelarEdicaoHorario();

        this.especialidadesLista.set([]);
        this.areasAtuacaoLista.set([]);
        this.especialidadesSelecionadas = [];
        this.especialidadeSelecionada = null;
        this.areaAtuacao = null;
    }

    private loadEspecialidades(): void {
        this.especialidadeService.listar().subscribe({
            next: (lista) => {
                const itens = (lista ?? []).map((item) => this.mapEspecialidadeListaItem(item)).filter((item): item is EspecialidadeListaItem => item !== null);
                this.especialidadesLista.set(itens);
            },
            error: () => this.especialidadesLista.set([])
        });
    }

    onEspecialidadeSelecionadaChange(value: number | null): void {
        this.especialidadeSelecionada = value;
        this.areaAtuacao = null;
        this.areasAtuacaoLista.set([]);
        if (value != null) {
            this.loadAreasAtuacao(value);
        }
    }

    private loadAreasAtuacao(idEspecialidade: number): void {
        this.especialidadeService.listarAreasAtuacaoPorEspecialidade(idEspecialidade).subscribe({
            next: (lista) => {
                const areas = Array.isArray(lista) ? lista : [];
                this.areasAtuacaoLista.set(areas);
            },
            error: () => {
                this.areasAtuacaoLista.set([]);
            }
        });
    }

    adicionarEspecialidade(): void {
        const idEspecialidade = this.especialidadeSelecionada;
        const idAreaAtuacao = this.areaAtuacao;
        if (idEspecialidade == null) {
            this.mostrarErroValidacao('Selecione a especialidade');
            return;
        }
        if (idAreaAtuacao == null) {
            this.mostrarErroValidacao('Selecione a área de atuação');
            return;
        }

        const especialidade = this.especialidadesLista().find((item) => item.idEspecialidade === idEspecialidade);
        if (!especialidade) {
            this.mostrarErroValidacao('Especialidade inválida');
            return;
        }
        const area = this.areasAtuacaoLista().find((item) => item.idAreaAtuacao === idAreaAtuacao);
        if (!area || area.idEspecArea == null) {
            this.mostrarErroValidacao('Área de atuação inválida');
            return;
        }
        if (this.especialidadesSelecionadas.some((item) => item.idEspecArea === area.idEspecArea)) {
            this.mostrarErroValidacao('Especialidade já adicionada');
            return;
        }

        const novoItem: EspecialidadeSelecionadaItem = {
            idEspecialidade: especialidade.idEspecialidade,
            dsEspecialidade: especialidade.dsEspecialidade,
            idAreaAtuacao: area.idAreaAtuacao ?? idAreaAtuacao,
            dsAreaAtuacao: area.dsAreaAtuacao ?? '',
            idEspecArea: area.idEspecArea
        };
        this.especialidadesSelecionadas = [...this.especialidadesSelecionadas, novoItem];
        this.areaAtuacao = null;
    }

    private atualizarLabelMunicipioSelecionado(idMunicipio: number | null): void {
        if (!idMunicipio) {
            this.municipioSelecionadoLabel.set('');
            return;
        }

        this.municipioService.buscarMunicipioPorId(idMunicipio).subscribe({
            next: (municipio) => {
                this.municipioSelecionadoLabel.set(municipio ? `${municipio.dsMunicipio} - ${municipio.uf ?? ''}`.trim() : '');
            },
            error: () => {
                this.municipioSelecionadoLabel.set('');
            }
        });
    }

    private mapEspecialidadeListaItem(item: EspecialidadeListaResponse): EspecialidadeListaItem | null {
        const idEspecialidade = item.idEspecialidade;
        if (idEspecialidade == null) return null;
        return {
            idEspecialidade,
            dsEspecialidade: item.dsEspecialidade ?? ''
        };
    }
}
